import PropTypes from 'prop-types';
import { useMemo } from 'react';
import CheckoutTaxStatus from './CheckoutTaxStatus';

function CheckoutCartTotals({
  cart = null,
  locale,
  retainedPaymentStatus = null,
  taxQuote = null,
}) {
  const subTotal = useMemo(
    () =>
      cart?.subtotal ??
      cart?.cartItemsAttributes?.reduce(
        (acc, item) => acc + item.price * item.quantity,
        0,
      ) ??
      0,
    [cart?.subtotal, cart?.cartItemsAttributes],
  );
  const shipping = cart?.shippingAmount || 0;
  const taxes = cart?.taxAmount || 0;
  const discountAmount = cart?.discountAmount || 0;
  const projectedAmountsReady =
    Boolean(retainedPaymentStatus) || taxQuote?.isReady === true;
  const total = taxQuote?.capability?.enabled
    ? (cart?.total ?? 0)
    : subTotal + shipping + taxes - discountAmount || cart?.total || 0;

  function formatAmount(amount) {
    return (amount / 100).toLocaleString(locale, {
      style: 'currency',
      currency: 'USD',
    });
  }

  return (
    <dl className="space-y-6 border-gray-200 border-t px-4 py-6 sm:px-6">
      <div className="flex items-center justify-between">
        <dt className="text-sm">Subtotal</dt>
        <dd className="font-medium text-gray-900 text-sm">
          {formatAmount(subTotal)}
        </dd>
      </div>
      <div className="flex items-center justify-between">
        <dt className="text-sm">Shipping</dt>
        <dd className="font-medium text-gray-900 text-sm">
          {projectedAmountsReady ? (
            formatAmount(shipping)
          ) : (
            <span>&mdash;</span>
          )}
        </dd>
      </div>
      <div className="flex items-center justify-between">
        <dt className="text-sm">Taxes</dt>
        <dd className="font-medium text-gray-900 text-sm">
          {projectedAmountsReady ? formatAmount(taxes) : <span>&mdash;</span>}
        </dd>
      </div>
      <CheckoutTaxStatus
        paused={Boolean(retainedPaymentStatus)}
        taxQuote={taxQuote}
      />
      {discountAmount > 0 ? (
        <div className="flex items-center justify-between">
          <dt className="text-sm">Discount</dt>
          <dd className="font-medium text-gray-900 text-sm">
            -{formatAmount(discountAmount)}
          </dd>
        </div>
      ) : null}
      <div className="flex items-center justify-between border-gray-200 border-t pt-6">
        <dt className="font-medium text-base">Total</dt>
        <dd className="font-medium text-base text-gray-900">
          {projectedAmountsReady ? formatAmount(total) : <span>&mdash;</span>}
        </dd>
      </div>
    </dl>
  );
}

CheckoutCartTotals.propTypes = {
  cart: PropTypes.shape({
    cartItemsAttributes: PropTypes.arrayOf(
      PropTypes.shape({
        price: PropTypes.number.isRequired,
        quantity: PropTypes.number.isRequired,
      }),
    ),
    discountAmount: PropTypes.number,
    shippingAmount: PropTypes.number,
    subtotal: PropTypes.number,
    taxAmount: PropTypes.number,
    total: PropTypes.number,
  }),
  locale: PropTypes.string.isRequired,
  retainedPaymentStatus: PropTypes.string,
  taxQuote: PropTypes.shape({
    capability: PropTypes.shape({
      enabled: PropTypes.bool.isRequired,
    }),
    errors: PropTypes.arrayOf(
      PropTypes.shape({
        message: PropTypes.string.isRequired,
        path: PropTypes.string,
      }),
    ),
    isReady: PropTypes.bool,
    status: PropTypes.string,
  }),
};

export default CheckoutCartTotals;
